/**
 * Scrollbar Plugin
 * Displays a proportional thumb scrollbar for carousel navigation
 *
 * Features:
 * - Proportional thumb width based on page count
 * - Real-time bidirectional sync with carousel track position
 * - Drag thumb to scrub through carousel
 * - Click track to navigate to nearest page
 * - Keyboard navigation (arrows, Home, End)
 * - Settle-on-release based on snap mode (page, slide, none)
 */

const MIN_THUMB_WIDTH_PX = 24;
const DRAG_CLICK_THRESHOLD_PX = 3;
const DRAG_CLICK_SUPPRESSION_MS = 120;

export default class Scrollbar {
	static pluginName = 'scrollbar';

	static defaultOptions = {
		scrollbar: {
			show: false,
			selector: false,
		}
	};

	// private drag state
	#targetPercent = 0;
	#lastSyncedPercent = -1;
	#rafId = null;
	#didMeaningfulDrag = false;
	#lastDragEndTs = 0;

	// cached geometry
	#thumbWidthPercent = 0;
	#maxTravelPercent = 100;

	constructor(ctx) {
		const _ = this;
		_.ctx = ctx;

		// plugin config
		_.scrollbarOptions = {};

		// DOM references
		_.scrollbarContainer = null;
		_.scrollbarTrack = null;
		_.scrollbarThumb = null;
		_.scrollbarGhost = null;
		_.isAutoGenerated = false;

		// drag state
		_.isDragging = false;
		_.dragStartX = 0;
		_.dragStartPercent = 0;
		_.activePointerId = null;
		_.pointerCaptureTarget = null;

		_.handlers = {
			// carousel events
			trackPositionChanged: (payload) => _.handleTrackPositionChanged(payload),
			optionsChanged: () => _.reInit(),
			layoutChanged: () => _.reInit(),
			pageCountChanged: () => _.reInit(),
			movementCompleted: () => _.handleMovementCompleted(),

			// pointer/touch events (passive: false for mobile)
			pointerDown: (event) => _.startDrag(event),
			pointerMove: (event) => _.handleDrag(event),
			pointerUp: (event) => _.endDrag(event),
			pointerCancel: (event) => _.endDrag(event),
			lostPointerCapture: (event) => _.handleLostPointerCapture(event),
			touchStart: (event) => _.handleTouchStart(event),
			touchMove: (event) => _.handleTouchMove(event),

			// click and keyboard
			trackClick: (event) => _.handleTrackClick(event),
			keyDown: (event) => _.handleKeyDown(event),
		};

		_.init();
	}

	init() {
		const _ = this;
		_.scrollbarOptions = _.ctx.store.getOptions().scrollbar || {};
		_.queryDOM();
		_.bindEvents();
		_.render();
	}

	reInit() {
		const _ = this;
		_.scrollbarOptions = _.ctx.store.getOptions().scrollbar || {};
		_.render();
	}

	queryDOM() {
		const _ = this;

		// use user-provided selector or auto-generate container
		if (_.scrollbarOptions.selector) {
			const element = document.querySelector(_.scrollbarOptions.selector);
			if (element) _.scrollbarContainer = element;
		}

		if (!_.scrollbarContainer) {
			_.isAutoGenerated = true;
			_.scrollbarContainer = document.createElement('div');
			_.ctx.carousel.appendChild(_.scrollbarContainer);
		}

		// setup container
		_.scrollbarContainer.classList.add('tarot-scrollbar');
		const carouselID = _.ctx.carousel.id || '';

		// clean up any plugin-owned nodes from prior mounts
		_.removeOwnedTrackNodes();

		// create track
		_.scrollbarTrack = document.createElement('div');
		_.scrollbarTrack.classList.add('tarot-scrollbar-track');
		_.scrollbarTrack.setAttribute('data-tarot-scrollbar-owned', 'true');
		if (carouselID) _.scrollbarTrack.setAttribute('data-tarot-scrollbar-owner', carouselID);
		_.scrollbarContainer.appendChild(_.scrollbarTrack);

		// create thumb
		_.scrollbarThumb = document.createElement('div');
		_.scrollbarThumb.classList.add('tarot-scrollbar-thumb');
		_.scrollbarThumb.setAttribute('role', 'slider');
		_.scrollbarThumb.setAttribute('tabindex', '0');
		_.scrollbarThumb.setAttribute('aria-label', 'Navigation thumb');
		if (carouselID) _.scrollbarThumb.setAttribute('aria-controls', carouselID);
		_.scrollbarTrack.appendChild(_.scrollbarThumb);

		// ghost thumb for portal loop effect
		_.scrollbarGhost = document.createElement('div');
		_.scrollbarGhost.classList.add('tarot-scrollbar-ghost');
		_.scrollbarGhost.setAttribute('aria-hidden', 'true');
		_.scrollbarTrack.appendChild(_.scrollbarGhost);
	}

	bindEvents() {
		const _ = this;
		const { emitter, events } = _.ctx;

		// carousel state sync
		emitter.on(events.track.positionChanged, _.handlers.trackPositionChanged);
		emitter.on(events.store.optionsChanged, _.handlers.optionsChanged);
		emitter.on(events.store.layoutChanged, _.handlers.layoutChanged);
		emitter.on(events.store.pageCountChanged, _.handlers.pageCountChanged);
		emitter.on(events.movement.completed, _.handlers.movementCompleted);

		// scrollbar track: pointer, touch, click (passive: false required for mobile)
		if (_.scrollbarTrack) {
			_.scrollbarTrack.addEventListener('pointerdown', _.handlers.pointerDown, { passive: false });
			_.scrollbarTrack.addEventListener('pointercancel', _.handlers.pointerCancel, { passive: false });
			_.scrollbarTrack.addEventListener('touchstart', _.handlers.touchStart, { passive: false });
			_.scrollbarTrack.addEventListener('touchmove', _.handlers.touchMove, { passive: false });
			_.scrollbarTrack.addEventListener('click', _.handlers.trackClick);
		}

		// keyboard nav on thumb
		if (_.scrollbarThumb) {
			_.scrollbarThumb.addEventListener('keydown', _.handlers.keyDown);
			_.scrollbarThumb.addEventListener('lostpointercapture', _.handlers.lostPointerCapture);
		}

		// ghost pointer capture events
		if (_.scrollbarGhost) {
			_.scrollbarGhost.addEventListener('lostpointercapture', _.handlers.lostPointerCapture);
		}
	}

	// remove plugin-owned scrollbar tracks inside the target container
	removeOwnedTrackNodes() {
		const _ = this;
		if (!_.scrollbarContainer) return;

		const carouselID = _.ctx.carousel.id || '';
		const tracks = _.scrollbarContainer.querySelectorAll(':scope > .tarot-scrollbar-track');

		tracks.forEach((track) => {
			if (track.getAttribute('data-tarot-scrollbar-owned') !== 'true') return;

			const owner = track.getAttribute('data-tarot-scrollbar-owner');
			if (owner && carouselID && owner !== carouselID) return;

			track.remove();
		});
	}

	render() {
		const _ = this;
		const state = _.ctx.store.getState();

		// hide if only one page or show is not explicitly true
		if (state.pageCount <= 1 || !_.scrollbarOptions.show) {
			_.hide();
			return;
		}

		_.show();
		_.updateThumbSize();

		const animation = _.ctx.store.getAnimation();
		_.syncToTrackPercent(animation.trackPercent || 0);
	}

	// ── Centralized continuous sync ─────────────────────────────────

	// route all visual sync through this single method
	syncToTrackPercent(trackPercent) {
		const _ = this;
		const canLoop = _.ctx.store.getState().canLoop;

		if (canLoop && trackPercent > 1) {
			_.activateGhost();
			_.updatePortalForward(trackPercent - 1);
		} else if (canLoop && trackPercent < 0) {
			_.activateGhost();
			_.updatePortalBackward(Math.abs(trackPercent));
		} else {
			_.deactivateGhost();
			_.updateThumbPosition(trackPercent);
		}

		_.updateAriaValues();
	}

	// sync thumb position when carousel moves (ignore during drag)
	handleTrackPositionChanged(payload) {
		if (this.isDragging) return;
		this.syncToTrackPercent(payload.trackPercent);
	}

	// sync scrollbar after animation completes
	handleMovementCompleted() {
		const _ = this;
		if (_.isDragging) return;

		const animation = _.ctx.store.getAnimation();
		const trackPercent = typeof animation.trackPercent === 'number' ? animation.trackPercent : 0;
		_.syncToTrackPercent(trackPercent);
	}

	// ── Canonical geometry ──────────────────────────────────────────

	// compute and cache thumb width + travel distance
	updateThumbSize() {
		const _ = this;
		if (!_.scrollbarThumb) return;

		const pageCount = Math.max(1, _.ctx.store.getState().pageCount);
		const trackWidth = _.scrollbarTrack ? _.scrollbarTrack.getBoundingClientRect().width : 0;

		let thumbWidthPercent = (1 / pageCount) * 100;
		if (trackWidth > 0) {
			const minPercent = (MIN_THUMB_WIDTH_PX / trackWidth) * 100;
			thumbWidthPercent = Math.max(thumbWidthPercent, minPercent);
		}
		thumbWidthPercent = Math.max(0, Math.min(100, thumbWidthPercent));

		_.#thumbWidthPercent = thumbWidthPercent;
		_.#maxTravelPercent = Math.max(0, 100 - thumbWidthPercent);

		_.scrollbarThumb.style.width = `${thumbWidthPercent}%`;
		if (_.scrollbarGhost) _.scrollbarGhost.style.width = `${thumbWidthPercent}%`;
	}

	// set thumb left position using cached travel distance
	updateThumbPosition(percent) {
		const _ = this;
		if (!_.scrollbarThumb) return;
		_.scrollbarThumb.style.left = `${percent * _.#maxTravelPercent}%`;
	}

	// ── Ghost visibility ────────────────────────────────────────────

	activateGhost() {
		if (!this.scrollbarGhost) return;
		this.scrollbarGhost.classList.add('tarot-scrollbar-ghost--active');
	}

	deactivateGhost() {
		const _ = this;
		if (!_.scrollbarGhost) return;
		_.scrollbarGhost.classList.remove('tarot-scrollbar-ghost--active');
		_.scrollbarGhost.style.left = '-200%';
	}

	// ── Portal loop helpers ─────────────────────────────────────────

	// forward loop: primary exits right, ghost enters from left
	updatePortalForward(loopProgress) {
		const _ = this;
		if (!_.scrollbarThumb || !_.scrollbarGhost) return;

		_.scrollbarThumb.style.left = `${_.#maxTravelPercent + loopProgress * _.#thumbWidthPercent}%`;
		_.scrollbarGhost.style.left = `${(loopProgress - 1) * _.#thumbWidthPercent}%`;
	}

	// backward loop: primary exits left, ghost enters from right
	updatePortalBackward(loopProgress) {
		const _ = this;
		if (!_.scrollbarThumb || !_.scrollbarGhost) return;

		_.scrollbarThumb.style.left = `${-loopProgress * _.#thumbWidthPercent}%`;
		_.scrollbarGhost.style.left = `${_.#maxTravelPercent + (1 - loopProgress) * _.#thumbWidthPercent}%`;
	}

	updateAriaValues() {
		const _ = this;
		if (!_.scrollbarThumb) return;
		const state = _.ctx.store.getState();
		_.scrollbarThumb.setAttribute('aria-valuemin', '0');
		_.scrollbarThumb.setAttribute('aria-valuemax', Math.max(0, state.pageCount - 1));
		_.scrollbarThumb.setAttribute('aria-valuenow', state.pageIndex);
		_.scrollbarThumb.setAttribute('aria-label', `Page ${state.pageIndex + 1} of ${state.pageCount}`);
	}

	// ── Touch handlers (mobile support) ──────────────────────────────

	// prevent browser from claiming thumb/ghost touch for page scrolling
	handleTouchStart(event) {
		if (event.target.closest('.tarot-scrollbar-thumb') || event.target.closest('.tarot-scrollbar-ghost')) {
			event.preventDefault();
		}
	}

	// prevent page scrolling while dragging thumb
	handleTouchMove(event) {
		if (this.isDragging) event.preventDefault();
	}

	// ── Drag lifecycle ───────────────────────────────────────────────

	#getTrackGeometry() {
		const _ = this;
		const state = _.ctx.store.getState();
		const widths = _.ctx.store.getWidths();
		const firstSnapPoint = Number.isFinite(state.firstSnapPoint) ? state.firstSnapPoint : 0;
		const lastSnapPoint = Number.isFinite(state.lastSnapPoint) ? state.lastSnapPoint : 0;
		const range = firstSnapPoint - lastSnapPoint;
		const trackWidth = Number.isFinite(widths.track) ? widths.track : 0;
		const loopRange = trackWidth - Math.abs(lastSnapPoint - firstSnapPoint);

		return {
			canLoop: state.canLoop,
			firstSnapPoint,
			lastSnapPoint,
			range,
			loopRange,
		};
	}

	#getPercentFromTrackPos(trackPosition) {
		const { canLoop, firstSnapPoint, lastSnapPoint, range, loopRange } = this.#getTrackGeometry();
		if (!Number.isFinite(trackPosition) || range === 0) return 0;

		const percent = (firstSnapPoint - trackPosition) / range;
		if (!canLoop || (percent >= 0 && percent <= 1) || loopRange <= 0) return percent;

		if (percent > 1) {
			const overflowDistance = Math.abs(trackPosition - lastSnapPoint);
			return 1 + overflowDistance / loopRange;
		}

		const overflowDistance = Math.abs(firstSnapPoint - trackPosition);
		return -(overflowDistance / loopRange);
	}

	#percentToDragCoord(percent, travelWidth, overflowWidth) {
		if (percent > 1) {
			return travelWidth + (percent - 1) * overflowWidth;
		}
		if (percent < 0) {
			return percent * overflowWidth;
		}
		return percent * travelWidth;
	}

	#dragCoordToPercent(coord, travelWidth, overflowWidth) {
		if (coord > travelWidth) {
			return 1 + (coord - travelWidth) / overflowWidth;
		}
		if (coord < 0) {
			return coord / overflowWidth;
		}
		return coord / travelWidth;
	}

	#getNearestPageForPercent(percent, state) {
		const pageCount = Math.max(1, state.pageCount);
		const lastPage = Math.max(0, pageCount - 1);
		if (pageCount <= 1) return 0;

		// In loop overflow ranges, percent represents a seam transition only
		// between the two edge pages (0 <-> last), not the full page range.
		if (state.canLoop && percent < 0) {
			const seamProgress = Math.min(1, Math.max(0, Math.abs(percent)));
			return seamProgress >= 0.5 ? lastPage : 0;
		}
		if (state.canLoop && percent > 1) {
			const seamProgress = Math.min(1, Math.max(0, percent - 1));
			return seamProgress >= 0.5 ? 0 : lastPage;
		}

		const clamped = Math.max(0, Math.min(1, percent));
		return Math.round(clamped * lastPage);
	}

	// pointerdown on thumb or ghost — begin drag
	startDrag(event) {
		const _ = this;
		const thumb = event.target.closest('.tarot-scrollbar-thumb');
		const ghost = event.target.closest('.tarot-scrollbar-ghost');
		if (!thumb && !ghost) return;

		event.preventDefault();
		event.stopPropagation();

		const grabTarget = thumb || ghost;
		_.activePointerId = typeof event.pointerId === 'number' ? event.pointerId : null;
		_.#didMeaningfulDrag = false;

		const animation = _.ctx.store.getAnimation();
		let trackPosition = animation.trackPosition ?? 0;
		let trackPercent = animation.trackPercent ?? _.#getPercentFromTrackPos(trackPosition);
		const state = _.ctx.store.getState();
		const pageCount = Math.max(1, state.pageCount);
		const options = _.ctx.store.getOptions();
		const snap = options.snap || 'page';

		_.isDragging = true;

		// normalize overflow drag start so scrollbar and track animator share the same baseline
		if (snap !== 'none' && state.canLoop && pageCount > 1 && (trackPercent > 1 || trackPercent < 0)) {
			const lastPage = pageCount - 1;
			let targetPage = 0;

			if (trackPercent > 1) {
				targetPage = ghost ? 0 : lastPage;
			} else {
				targetPage = ghost ? lastPage : 0;
			}

			_.ctx.commands.jumpToPage(targetPage);

			const freshAnimation = _.ctx.store.getAnimation();
			trackPosition = freshAnimation.trackPosition ?? 0;
			trackPercent = freshAnimation.trackPercent ?? _.#getPercentFromTrackPos(trackPosition);
			_.syncToTrackPercent(trackPercent);
		}

		_.dragStartX = event.clientX;
		_.dragStartPercent = trackPercent;
		_.#targetPercent = trackPercent;
		_.#lastSyncedPercent = -1;

		// capture pointer on the grabbed element
		_.pointerCaptureTarget = grabTarget;
		grabTarget.setPointerCapture(event.pointerId);

		// listen on document for move/up (pointer capture ensures delivery)
		document.addEventListener('pointermove', _.handlers.pointerMove);
		document.addEventListener('pointerup', _.handlers.pointerUp);
		document.addEventListener('pointercancel', _.handlers.pointerCancel);

		_.scrollbarContainer.classList.add('tarot-scrollbar--dragging');

		_.ctx.emitter.emit(_.ctx.events.user.interacted, { via: 'scrollbar', event });

		// start rAF coalescing loop
		_.#rafId = requestAnimationFrame(() => _.#tick());
	}

	// pointermove — update target percent and thumb CSS immediately
	// actual carousel sync is coalesced via rAF in #tick()
	handleDrag(event) {
		const _ = this;
		if (!_.isDragging || !_.scrollbarTrack) return;
		if (_.activePointerId !== null && event.pointerId !== _.activePointerId) return;

		event.preventDefault();

		const trackRect = _.scrollbarTrack.getBoundingClientRect();
		const travelWidth = trackRect.width * (_.#maxTravelPercent / 100);
		const overflowWidth = trackRect.width * (_.#thumbWidthPercent / 100);
		if (travelWidth <= 0 || overflowWidth <= 0) return;

		const deltaX = event.clientX - _.dragStartX;
		if (!_.#didMeaningfulDrag && Math.abs(deltaX) > DRAG_CLICK_THRESHOLD_PX) {
			_.#didMeaningfulDrag = true;
		}
		const dragStartCoord = _.#percentToDragCoord(_.dragStartPercent, travelWidth, overflowWidth);
		const dragCoord = dragStartCoord + deltaX;
		const rawPercent = _.#dragCoordToPercent(dragCoord, travelWidth, overflowWidth);
		const canLoop = _.ctx.store.getState().canLoop;
		const newPercent = canLoop ? rawPercent : Math.max(0, Math.min(1, rawPercent));

		_.#targetPercent = newPercent;
		_.syncToTrackPercent(newPercent);
	}

	handleLostPointerCapture(event) {
		const _ = this;
		if (!_.isDragging) return;
		if (_.activePointerId !== null && event.pointerId !== _.activePointerId) return;
		_.endDrag(event);
	}

	#removeDocumentDragListeners() {
		const _ = this;
		document.removeEventListener('pointermove', _.handlers.pointerMove);
		document.removeEventListener('pointerup', _.handlers.pointerUp);
		document.removeEventListener('pointercancel', _.handlers.pointerCancel);
	}

	#releasePointerCapture(pointerId) {
		const _ = this;
		const id = typeof pointerId === 'number' ? pointerId : _.activePointerId;
		if (id === null) return;

		try {
			const target = _.pointerCaptureTarget || _.scrollbarThumb;
			if (target && target.hasPointerCapture(id)) {
				target.releasePointerCapture(id);
			}
		} catch {
			// element may have been detached from DOM
		}
		_.pointerCaptureTarget = null;
	}

	// rAF loop — coalesces pointermove events into one store update per frame
	#tick() {
		const _ = this;
		if (!_.isDragging) return;

		// sync carousel if percent changed since last frame
		if (_.#targetPercent !== _.#lastSyncedPercent) {
			_.ctx.commands.driveTrackPosition({ positionPercent: _.#targetPercent });
			_.#lastSyncedPercent = _.#targetPercent;
		}

		_.#rafId = requestAnimationFrame(() => _.#tick());
	}

	// pointerup / pointercancel — end drag and snap to nearest page
	endDrag(event) {
		const _ = this;
		if (!_.isDragging) return;
		if (_.activePointerId !== null && event?.pointerId !== undefined && event.pointerId !== _.activePointerId) return;

		_.isDragging = false;
		_.#lastDragEndTs = typeof performance !== 'undefined' && typeof performance.now === 'function'
			? performance.now()
			: Date.now();

		// stop rAF loop
		if (_.#rafId !== null) {
			cancelAnimationFrame(_.#rafId);
			_.#rafId = null;
		}

		// flush any pending percent change
		if (_.#targetPercent !== _.#lastSyncedPercent) {
			_.ctx.commands.driveTrackPosition({ positionPercent: _.#targetPercent });
		}

		// clean up pointer listeners
		_.#removeDocumentDragListeners();

		// release pointer capture (may already be released on pointercancel)
		_.#releasePointerCapture(event?.pointerId);
		_.activePointerId = null;
		_.scrollbarContainer.classList.remove('tarot-scrollbar--dragging');

		// settle to nearest snap point based on snap mode
		const options = _.ctx.store.getOptions();
		const snap = options.snap || 'page';
		if (snap === 'page') {
			_.ctx.commands.driveTrackPosition({ movementType: 'settle' });
			const state = _.ctx.store.getState();
			const nearestPage = _.#getNearestPageForPercent(_.#targetPercent, state);
			_.ctx.commands.goToPage(nearestPage);
		} else if (snap === 'slide') {
			_.ctx.commands.driveTrackPosition({ movementType: 'settle' });
		}
		// snap === 'none': no action needed, thumb stays where released
	}

	// ── Track click & keyboard ───────────────────────────────────────

	// click on track (not thumb/ghost) — jump to nearest page at click position
	handleTrackClick(event) {
		const _ = this;
		if (event.target.closest('.tarot-scrollbar-thumb') || event.target.closest('.tarot-scrollbar-ghost')) return;

		const now =
			typeof performance !== 'undefined' && typeof performance.now === 'function'
				? performance.now()
				: Date.now();
		const elapsedSinceDragEnd = now - _.#lastDragEndTs;
		if (_.#didMeaningfulDrag) {
			_.#didMeaningfulDrag = false;
			if (elapsedSinceDragEnd <= DRAG_CLICK_SUPPRESSION_MS) {
				event.preventDefault();
				event.stopPropagation();
				return;
			}
		}

		event.preventDefault();
		event.stopPropagation();

		const trackRect = _.scrollbarTrack.getBoundingClientRect();
		if (trackRect.width <= 0) return;

		const pageCount = _.ctx.store.getState().pageCount;
		if (pageCount <= 1) return;

		const clickPercent = Math.max(0, Math.min(1, (event.clientX - trackRect.left) / trackRect.width));

		const nearestPage = Math.round(clickPercent * (pageCount - 1));

		_.ctx.emitter.emit(_.ctx.events.user.interacted, { via: 'scrollbar', event });
		_.ctx.commands.goToPage(nearestPage);
	}

	// arrow keys, Home, End on focused thumb
	handleKeyDown(event) {
		const _ = this;
		const allowedKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
		if (!allowedKeys.includes(event.key)) return;

		event.preventDefault();
		event.stopPropagation();

		const state = _.ctx.store.getState();
		let newPage = state.pageIndex;

		switch (event.key) {
			case 'ArrowRight':
				if (state.canLoop && state.pageIndex >= state.pageCount - 1) {
					newPage = 0;
				} else {
					newPage = Math.min(state.pageCount - 1, state.pageIndex + 1);
				}
				break;
			case 'ArrowLeft':
				if (state.canLoop && state.pageIndex <= 0) {
					newPage = state.pageCount - 1;
				} else {
					newPage = Math.max(0, state.pageIndex - 1);
				}
				break;
			case 'Home':
				newPage = 0;
				break;
			case 'End':
				newPage = state.pageCount - 1;
				break;
		}

		_.ctx.emitter.emit(_.ctx.events.user.interacted, { via: 'key', event });
		_.ctx.commands.goToPage(newPage);
	}

	// ── Visibility ───────────────────────────────────────────────────

	show() {
		this.scrollbarContainer.style.display = '';
	}

	hide() {
		this.scrollbarContainer.style.display = 'none';
	}

	// ── Cleanup ──────────────────────────────────────────────────────

	destroy() {
		const _ = this;
		const { emitter, events } = _.ctx;

		// cancel rAF if running
		if (_.#rafId !== null) {
			cancelAnimationFrame(_.#rafId);
			_.#rafId = null;
		}

		// carousel events
		emitter.off(events.track.positionChanged, _.handlers.trackPositionChanged);
		emitter.off(events.store.optionsChanged, _.handlers.optionsChanged);
		emitter.off(events.store.layoutChanged, _.handlers.layoutChanged);
		emitter.off(events.store.pageCountChanged, _.handlers.pageCountChanged);
		emitter.off(events.movement.completed, _.handlers.movementCompleted);

		// DOM events
		if (_.scrollbarTrack) {
			_.scrollbarTrack.removeEventListener('pointerdown', _.handlers.pointerDown);
			_.scrollbarTrack.removeEventListener('pointercancel', _.handlers.pointerCancel);
			_.scrollbarTrack.removeEventListener('touchstart', _.handlers.touchStart);
			_.scrollbarTrack.removeEventListener('touchmove', _.handlers.touchMove);
			_.scrollbarTrack.removeEventListener('click', _.handlers.trackClick);
		}
		if (_.scrollbarThumb) {
			_.scrollbarThumb.removeEventListener('keydown', _.handlers.keyDown);
			_.scrollbarThumb.removeEventListener('lostpointercapture', _.handlers.lostPointerCapture);
		}
		if (_.scrollbarGhost) {
			_.scrollbarGhost.removeEventListener('lostpointercapture', _.handlers.lostPointerCapture);
		}

		_.#removeDocumentDragListeners();
		_.#releasePointerCapture();
		_.activePointerId = null;
		_.pointerCaptureTarget = null;

		// remove plugin-created DOM
		if (_.scrollbarTrack && _.scrollbarTrack.parentNode === _.scrollbarContainer) {
			_.scrollbarTrack.remove();
		} else if (_.scrollbarContainer) {
			_.removeOwnedTrackNodes();
		}

		if (_.isAutoGenerated && _.scrollbarContainer && _.scrollbarContainer.parentNode === _.ctx.carousel) {
			_.ctx.carousel.removeChild(_.scrollbarContainer);
		}

		_.scrollbarContainer = null;
		_.scrollbarTrack = null;
		_.scrollbarThumb = null;
		_.scrollbarGhost = null;
	}
}

// queue the plugin (works regardless of whether Tarot is loaded)
(window.TarotPluginQueue = window.TarotPluginQueue || []).push({
	pluginName: 'scrollbar',
	plugin: Scrollbar
});
